/**
 * App Shell Skeleton
 * Shows skeleton content with static TabBar for offline/loading states
 * Used as fallback for lazy-loaded pages that have TabBar
 */

import { Skeleton } from '@/components/ui/skeleton';

interface AppShellSkeletonProps {
  showHeader?: boolean;
  headerTitle?: string;
  contentType?: 'cards' | 'list' | 'default';
}

const StaticTabBar = () => (
  <nav
    className="fixed bottom-0 left-0 right-0 z-50 flex justify-center bg-bg-primary border-t border-border-tertiary"
    style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
  >
    <div className="w-full max-w-[420px] flex items-center justify-around h-[56px]">
      {/* Friends tab */}
      <div className="flex flex-col items-center justify-center min-w-[80px] min-h-[44px] py-1 opacity-40">
        <div className="w-6 h-6 mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full text-text-primary">
            <path d="M4.5 21.5v-3a5 5 0 0 1 5-5h5a5 5 0 0 1 5 5v3" />
            <circle cx="12" cy="7.5" r="4" />
          </svg>
        </div>
        <span className="text-[11px] font-medium text-text-primary">Друзья</span>
      </div>
      
      {/* Chats tab */}
      <div className="flex flex-col items-center justify-center min-w-[80px] min-h-[44px] py-1 opacity-40">
        <div className="w-6 h-6 mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full text-text-primary">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </div>
        <span className="text-[11px] font-medium text-text-primary">Чаты</span>
      </div>
      
      {/* Favorites tab */}
      <div className="flex flex-col items-center justify-center min-w-[80px] min-h-[44px] py-1 opacity-40">
        <div className="w-6 h-6 mb-0.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full text-text-primary">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </div>
        <span className="text-[11px] font-medium text-text-primary">Избранное</span>
      </div>
    </div>
  </nav>
);

const CardsSkeleton = () => (
  <div className="grid grid-cols-2 gap-3 px-4">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="rounded-[var(--radius-card)] overflow-hidden">
        <Skeleton className="aspect-[3/4] w-full" />
      </div>
    ))}
  </div>
);

const ListSkeleton = () => (
  <div className="flex flex-col gap-3 px-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-bg-secondary">
        <Skeleton className="w-12 h-12 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const DefaultSkeleton = () => (
  <div className="flex flex-col items-center justify-center flex-1 gap-4 px-4">
    <Skeleton className="h-12 w-12 rounded-full" />
    <Skeleton className="h-4 w-32" />
  </div>
);

export function AppShellSkeleton({ 
  showHeader = true, 
  headerTitle,
  contentType = 'default' 
}: AppShellSkeletonProps) {
  return (
    <div className="min-h-screen bg-bg-primary flex flex-col">
      {/* Header */}
      {showHeader && (
        <header className="sticky top-0 z-40 bg-bg-primary border-b border-border-tertiary/8 px-4 py-3">
          <div className="flex items-center justify-between max-w-[420px] mx-auto">
            {headerTitle ? (
              <h1 className="text-lg font-medium text-text-primary">{headerTitle}</h1>
            ) : (
              <Skeleton className="h-6 w-24" />
            )}
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </header>
      )}
      
      {/* Content */}
      <main className="flex-1 pb-[calc(72px+env(safe-area-inset-bottom))] pt-4">
        {contentType === 'cards' && <CardsSkeleton />}
        {contentType === 'list' && <ListSkeleton />}
        {contentType === 'default' && <DefaultSkeleton />}
      </main>
      
      {/* Static TabBar */}
      <StaticTabBar />
    </div>
  );
}

export default AppShellSkeleton;
